import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Database, RefreshCw, Search, Eye, Upload } from "lucide-react";
import { slidesList, slidesCreate, slidesUpdate, slidesDelete } from "@/lib/adminApi";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProfileView from "@/components/admin/ProfileView";
import ImportDialog from "@/components/admin/ImportDialog";
import TvormenEditor from "@/components/admin/TvormenEditor";

// Beheerinterface bovenop de bestaande Supabase-tabellen (slideshow-systeem).
// Relaties: Producten.inhoudsprofiel_id -> Inhoudsprofielen -> Werkzame stoffen (werkzame_stof_id)
//           Profielinhoud.inhoudsprofiel_id -> Inhoudsprofielen
const INPUT_CLS =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0a5a3c]";

const PROFIEL_TITELS = ["indicatie", "gebruik", "dosering", "praktische_info"];
const PER_PAGE = 25;

// Specifieke farmaceutische vormen (suggesties bij het aanmaken van vormprofielen)
import { VORM_OPTIES } from "@/lib/vormen";

const VORMEN = VORM_OPTIES;

const ENTITIES = [
  {
    key: "apotheken",
    label: "Apotheken",
    searchPlaceholder: "Zoek op naam, URL-naam, e-mail…",
    filtersDef: [],
    fields: [
      { key: "naam", label: "Naam", required: true },
      { key: "url_naam", label: "URL-naam", required: true, hint: "wordt gebruikt in de slideshow-link, bv. apotheek-mijlbeek" },
      { key: "logo_url", label: "Logo URL" },
      { key: "hoofdkleur", label: "Hoofdkleur", type: "color" },
      { key: "achtergrondkleur", label: "Achtergrondkleur", type: "color" },
      { key: "tekst_kleur", label: "Tekstkleur", type: "color" },
      { key: "lettertype", label: "Lettertype", hint: "bv. Arial, Poppins, Montserrat" },
      { key: "telefoon", label: "Telefoon" },
      { key: "email", label: "E-mail" },
      { key: "website", label: "Website" },
      { key: "openingsuren", label: "Openingsuren", type: "textarea" },
    ],
    columns: [
      { key: "naam", label: "Naam" },
      { key: "url_naam", label: "URL-naam" },
      { key: "_kleuren", label: "Huisstijl" },
      { key: "telefoon", label: "Telefoon" },
      { key: "email", label: "E-mail" },
    ],
  },
  {
    key: "producten",
    label: "Producten",
    searchPlaceholder: "Zoek op CNK, productnaam, merk…",
    filtersDef: [
      { key: "inhoudsprofiel_id", label: "Inhoudsprofiel", source: "inhoudsprofielen" },
    ],
    fields: [
      { key: "naam", label: "Naam", required: true },
      { key: "cnk", label: "CNK-code", required: true, hint: "wordt gebruikt in de slideshow-link" },
      { key: "merk", label: "Merk" },
      { key: "vorm", label: "Vorm", hint: "bv. Tablet, Zetpil, Siroop" },
      { key: "werkzame_stof", label: "Werkzame stof" },
      { key: "afbeelding_url", label: "Afbeelding URL" },
      { key: "inhoudsprofiel_id", label: "Inhoudsprofiel", type: "select", source: "inhoudsprofielen" },
    ],
    columns: [
      { key: "naam", label: "Naam" },
      { key: "cnk", label: "CNK" },
      { key: "werkzame_stof", label: "Werkzame stof" },
      { key: "inhoudsprofiel_id", label: "Inhoudsprofiel", ref: "inhoudsprofielen" },
    ],
  },
  {
    key: "werkzame-stoffen",
    label: "Werkzame stoffen",
    searchPlaceholder: "Zoek op naam of inhoud…",
    filtersDef: [],
    fields: [
      { key: "naam", label: "Naam", required: true, hint: "bv. Ibuprofen" },
      { key: "werking", label: "Werking", type: "textarea" },
      { key: "bijwerkingen_vaak", label: "Bijwerkingen — vaak", type: "textarea" },
      { key: "bijwerkingen_ernstig", label: "Bijwerkingen — ernstig (hulp zoeken)", type: "textarea" },
      { key: "algemene_waarschuwingen", label: "Algemene waarschuwingen", type: "textarea", hint: "een lege regel = nieuwe bullet op de slide" },
      { key: "gebruik", label: "Gebruik", type: "textarea", hint: "algemene gebruiksinstructie voor deze stof — verschijnt als het profiel er geen heeft" },
      { key: "praktische_info", label: "Praktische info", type: "textarea", hint: "algemene praktische tips — verschijnt als het profiel er geen heeft" },
      { key: "toedieningsvormen", label: "Toedieningsvormen", type: "tvormen" },
    ],
    columns: [
      { key: "naam", label: "Naam" },
      { key: "werking", label: "Werking", truncate: true },
      { key: "_tvormen", label: "Vormen" },
    ],
  },
  {
    key: "inhoudsprofielen",
    label: "Inhoudsprofielen",
    searchPlaceholder: "Zoek op profielnaam, stof, sterkte…",
    filtersDef: [
      { key: "werkzame_stof_id", label: "Werkzame stof", source: "werkzame-stoffen" },
    ],
    fields: [
      { key: "naam", label: "Naam", required: true, hint: "bv. Ibuprofen 400 mg tablet" },
      { key: "werkzame_stof", label: "Werkzame stof (tekst)" },
      { key: "sterkte", label: "Sterkte", hint: "bv. 400 mg" },
      { key: "vorm", label: "Vorm" },
      { key: "werkzame_stof_id", label: "Gekoppelde werkzame stof", type: "select", source: "werkzame-stoffen" },
      { key: "toedieningsvorm", label: "Toedieningsvorm (specifiek)", type: "datalist",
        options: VORMEN, hint: "leeg = basisprofiel (algemene teksten). Bv. tablet, zetpil, injectie, inhalatiepoeder…" },
    ],
    columns: [
      { key: "naam", label: "Naam" },
      { key: "toedieningsvorm", label: "Vorm" },
      { key: "sterkte", label: "Sterkte" },
      { key: "vorm", label: "Vorm (tekst)" },
      { key: "werkzame_stof_id", label: "Werkzame stof", ref: "werkzame-stoffen" },
    ],
  },
  {
    key: "profielinhoud",
    label: "Profielinhoud",
    searchPlaceholder: "Zoek op onderdeel of inhoud…",
    filtersDef: [
      { key: "inhoudsprofiel_id", label: "Inhoudsprofiel", source: "inhoudsprofielen" },
      { key: "titel", label: "Onderdeel", options: PROFIEL_TITELS },
    ],
    fields: [
      { key: "inhoudsprofiel_id", label: "Inhoudsprofiel", type: "select", source: "inhoudsprofielen", required: true },
      { key: "titel", label: "Onderdeel", type: "datalist", options: PROFIEL_TITELS, required: true },
      { key: "volgorde", label: "Volgorde", type: "number" },
      { key: "inhoud", label: "Inhoud", type: "textarea" },
      { key: "afbeelding_url", label: "Afbeelding URL (optioneel)" },
      { key: "video_url", label: "Video URL (optioneel)" },
    ],
    columns: [
      { key: "inhoudsprofiel_id", label: "Inhoudsprofiel", ref: "inhoudsprofielen" },
      { key: "titel", label: "Onderdeel" },
      { key: "volgorde", label: "Volgorde" },
      { key: "inhoud", label: "Inhoud", truncate: true },
    ],
  },
];

const REF_SOURCES = ["inhoudsprofielen", "werkzame-stoffen"];

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const Pagination = ({ page, perPage, total, onPage }) => {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500" data-testid="slides-pagination">
      <span data-testid="slides-total">{total === 1 ? "1 resultaat" : `${total} resultaten`} · pagina {page}/{pages}</span>
      <div className="flex gap-2">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} data-testid="slides-page-prev"
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">
          Vorige
        </button>
        <button onClick={() => onPage(page + 1)} disabled={page >= pages} data-testid="slides-page-next"
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">
          Volgende
        </button>
      </div>
    </div>
  );
};

const SlidesManager = () => {
  const [entity, setEntity] = useState("apotheken");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [refs, setRefs] = useState({ "inhoudsprofielen": [], "werkzame-stoffen": [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { entity, row|null }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [profileView, setProfileView] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [importOpen, setImportOpen] = useState(false);

  const dq = useDebounced(q, 300);
  const cfg = useMemo(() => ENTITIES.find((e) => e.key === entity), [entity]);
  const dialogCfg = useMemo(
    () => (dialog ? ENTITIES.find((e) => e.key === dialog.entity) : null),
    [dialog]
  );

  const loadRefs = useCallback(async () => {
    try {
      const results = await Promise.all(REF_SOURCES.map((r) => slidesList(r, { per_page: 1000 })));
      setRefs(Object.fromEntries(REF_SOURCES.map((r, i) => [r, results[i].rows || []])));
    } catch { /* refs zijn enkel voor dropdowns */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await slidesList(entity, { q: dq, page, per_page: PER_PAGE, ...filterVals });
      setRows(d.rows || []);
      setTotal(d.total ?? 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon gegevens niet laden");
    }
    setLoading(false);
  }, [entity, dq, page, filterVals]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load, reloadToken]);

  const switchEntity = (key) => {
    setEntity(key);
    setQ("");
    setFilterVals({});
    setPage(1);
    setProfileView(null);
  };

  const refName = (source, id) => {
    if (id === null || id === undefined || id === "") return "-";
    const hit = (refs[source] || []).find((r) => r.id === id);
    return hit ? hit.naam : `#${id}`;
  };

  const openDialog = (entityKey, row = null, prefill = {}) => {
    const c = ENTITIES.find((e) => e.key === entityKey);
    setDialog({ entity: entityKey, row });
    setForm(Object.fromEntries(
      c.fields.map((f) => [f.key, row
        ? (f.type === "tvormen" ? row[f.key] ?? [] : row[f.key] ?? "")
        : prefill[f.key] ?? (f.type === "tvormen" ? [] : "")])
    ));
  };

  const save = async (e) => {
    e.preventDefault();
    for (const f of dialogCfg.fields) {
      if (f.required && !String(form[f.key] ?? "").trim()) {
        return toast.error(`Veld "${f.label}" is verplicht`);
      }
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        dialogCfg.fields.map((f) => [f.key, form[f.key] ?? (f.type === "tvormen" ? [] : "")])
      );
      if (dialog.row) {
        await slidesUpdate(dialog.entity, dialog.row.id, payload);
        toast.success("Wijziging opgeslagen");
      } else {
        await slidesCreate(dialog.entity, payload);
        toast.success("Toegevoegd");
      }
      setDialog(null);
      setReloadToken((t) => t + 1);
      if (REF_SOURCES.includes(dialog.entity)) await loadRefs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    }
    setSaving(false);
  };

  const remove = async (entityKey, row, label) => {
    if (!window.confirm(`${label} verwijderen? Dit kan niet ongedaan worden.`)) return;
    try {
      await slidesDelete(entityKey, row.id);
      toast.success("Verwijderd");
      if (profileView && entityKey === "inhoudsprofielen" && row.id === profileView.id) setProfileView(null);
      setReloadToken((t) => t + 1);
      if (REF_SOURCES.includes(entityKey)) await loadRefs();
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const renderCell = (row, col) => {
    if (col.key === "_kleuren") {
      return (
        <div className="flex items-center gap-1.5">
          {[row.hoofdkleur, row.achtergrondkleur, row.tekst_kleur].map((c, i) =>
            c ? <span key={i} title={c} className="h-4 w-4 rounded-full border border-slate-200" style={{ background: c }} /> : null
          )}
        </div>
      );
    }
    if (col.key === "_tvormen") {
      const n = Array.isArray(row.toedieningsvormen) ? row.toedieningsvormen.length : 0;
      return n ? `${n} vorm${n === 1 ? "" : "en"}` : <span className="text-slate-300">-</span>;
    }
    if (col.ref) return refName(col.ref, row[col.key]);
    const v = row[col.key];
    if (v === null || v === undefined || v === "") return <span className="text-slate-300">-</span>;
    const s = String(v);
    return col.truncate && s.length > 60 ? `${s.slice(0, 60)}…` : s;
  };

  const renderField = (f) => {
    const val = form[f.key] ?? (f.type === "tvormen" ? [] : "");
    if (f.type === "tvormen") {
      return <TvormenEditor key={f.key} value={val}
        onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} />;
    }
    const common = {
      id: `slides-field-${f.key}`,
      "data-testid": `slides-field-${f.key}`,
      value: val,
      onChange: (e) => setForm((p) => ({ ...p, [f.key]: e.target.value })),
    };
    return (
      <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
        <label htmlFor={common.id} className="mb-1 block text-xs font-medium text-slate-500">
          {f.label}{f.required ? " *" : ""}
        </label>
        {f.type === "textarea" ? (
          <textarea {...common} rows={3} className={INPUT_CLS} />
        ) : f.type === "select" ? (
          <select {...common} className={INPUT_CLS}>
            <option value="">— Geen —</option>
            {(refs[f.source] || []).map((r) => (
              <option key={r.id} value={r.id}>{r.naam}</option>
            ))}
          </select>
        ) : f.type === "datalist" ? (
          <>
            <input {...common} list={`list-${f.key}`} className={INPUT_CLS} />
            <datalist id={`list-${f.key}`}>
              {f.options.map((o) => <option key={o} value={o} />)}
            </datalist>
          </>
        ) : f.type === "number" ? (
          <input {...common} type="number" className={INPUT_CLS} />
        ) : f.type === "color" ? (
          <div className="flex items-center gap-2">
            <input {...common} placeholder="#DDE8B8" className={INPUT_CLS} />
            <span className="h-8 w-8 flex-shrink-0 rounded-lg border border-slate-200" style={{ background: val || "transparent" }} />
          </div>
        ) : (
          <input {...common} className={INPUT_CLS} />
        )}
        {f.hint && <p className="mt-1 text-[11px] text-slate-400">{f.hint}</p>}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="slides-manager">
      {/* entity switcher */}
      <div className="flex flex-wrap items-center gap-2" data-testid="slides-entities">
        {ENTITIES.map((e) => (
          <button key={e.key} onClick={() => switchEntity(e.key)} data-testid={`slides-entity-${e.key}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              entity === e.key && !profileView
                ? "bg-[#0a2540] text-white"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}>
            {e.label}
          </button>
        ))}
      </div>

      {profileView ? (
        <ProfileView
          profile={profileView}
          refs={refs}
          onBack={() => setProfileView(null)}
          openDialog={openDialog}
          onDelete={remove}
          reloadToken={reloadToken}
        />
      ) : (
        <>
          {/* zoeken + filters + acties */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder={cfg.searchPlaceholder}
                data-testid="slides-search"
                className={`${INPUT_CLS} pl-9`}
              />
            </div>
            {cfg.filtersDef.map((f) => (
              <select
                key={f.key}
                value={filterVals[f.key] ?? ""}
                onChange={(e) => { setFilterVals((p) => ({ ...p, [f.key]: e.target.value })); setPage(1); }}
                data-testid={`slides-filter-${f.key}`}
                className={`${INPUT_CLS} w-auto min-w-[160px]`}
              >
                <option value="">Alle: {f.label}</option>
                {f.source
                  ? (refs[f.source] || []).map((r) => <option key={r.id} value={r.id}>{r.naam}</option>)
                  : f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <button onClick={load} data-testid="slides-refresh"
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <RefreshCw className="h-4 w-4" /> Vernieuwen
              </button>
              <button onClick={() => setImportOpen(true)} data-testid="slides-import-btn"
                className="flex items-center gap-1.5 rounded-lg border border-[#0a5a3c]/30 bg-white px-3 py-2 text-sm font-semibold text-[#0a5a3c] hover:bg-[#0a5a3c]/5">
                <Upload className="h-4 w-4" /> Bulk import
              </button>
              <button onClick={() => openDialog(entity)} data-testid="slides-add-btn"
                className="flex items-center gap-1.5 rounded-lg bg-[#0a5a3c] px-3 py-2 text-sm font-semibold text-white hover:bg-[#084a31]">
                <Plus className="h-4 w-4" /> Nieuw
              </button>
            </div>
          </div>

          {/* list */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="slides-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {cfg.columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
                  <th className="px-4 py-3">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={cfg.columns.length + 1} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={cfg.columns.length + 1} className="px-4 py-10 text-center text-slate-400">
                    <Database className="mx-auto mb-2 h-5 w-5 text-slate-300" />Geen resultaten
                  </td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id} data-testid={`slides-row-${row.id}`} className="hover:bg-slate-50">
                    {cfg.columns.map((c) => (
                      <td key={c.key} className="max-w-xs px-4 py-3 text-slate-600 first:font-medium first:text-[#0a2540]">
                        {renderCell(row, c)}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {entity === "inhoudsprofielen" && (
                          <button onClick={() => setProfileView(row)} data-testid={`slides-profile-${row.id}`} title="Bekijk profiel"
                            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a5a3c] hover:bg-[#0a5a3c]/5">
                            <Eye className="h-3.5 w-3.5" /> Profiel
                          </button>
                        )}
                        <button onClick={() => openDialog(entity, row)} data-testid={`slides-edit-${row.id}`} title="Bewerken"
                          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-slate-50">
                          <Pencil className="h-3.5 w-3.5" /> Bewerk
                        </button>
                        <button onClick={() => remove(entity, row, `"${row.naam || row.titel || `#${row.id}`}"`)} data-testid={`slides-delete-${row.id}`} title="Verwijderen"
                          className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} perPage={PER_PAGE} total={total} onPage={setPage} />
          </div>
        </>
      )}

      <ImportDialog open={importOpen} onOpenChange={setImportOpen}
        onDone={() => { setReloadToken((t) => t + 1); loadRefs(); }} />

      {/* create/edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="slides-dialog">
          {dialogCfg && (
            <>
              <DialogHeader>
                <DialogTitle>{dialog.row ? "Bewerk" : "Nieuw"} — {dialogCfg.label}</DialogTitle>
                <DialogDescription className="sr-only">Formulier om een {dialogCfg.label.toLowerCase()}-rij toe te voegen of te wijzigen</DialogDescription>
              </DialogHeader>
              <form onSubmit={save} className="mt-2 grid gap-4 sm:grid-cols-2" data-testid="slides-form">
                {dialogCfg.fields.map(renderField)}
                <div className="flex justify-end gap-2 sm:col-span-2">
                  <button type="button" onClick={() => setDialog(null)} data-testid="slides-cancel"
                    className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    Annuleren
                  </button>
                  <button type="submit" disabled={saving} data-testid="slides-save-btn"
                    className="flex items-center gap-2 rounded-lg bg-[#0a2540] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0a1f38] disabled:opacity-50">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {dialog.row ? "Opslaan" : "Toevoegen"}
                  </button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SlidesManager;
